import { useCallback, useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ArrowLeft, Trash2, Mail, Inbox } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useDocumentMeta } from "@/hooks/useDocumentMeta";

export default function AdminEnquiries() {
  const navigate = useNavigate();
  const [items, setItems] = useState(null);

  useDocumentMeta({ title: "Enquiries", path: "/admin/enquiries" });

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/api/admin/enquiries");
      setItems(data);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed to load enquiries");
      setItems([]);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const remove = async (item) => {
    if (!window.confirm(`Delete enquiry from ${item.email}?`)) return;
    try {
      await api.delete(`/api/admin/enquiries/${item.id}`);
      toast.success("Enquiry deleted");
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Delete failed");
    }
  };

  return (
    <div data-testid="admin-enquiries" className="min-h-screen bg-[#F1F1EE]">
      <header className="bg-charcoal text-canvas">
        <div className="max-w-[1000px] mx-auto px-6 lg:px-10 h-20 flex items-center justify-between">
          <button
            onClick={() => navigate("/admin")}
            data-testid="back-to-dashboard"
            className="inline-flex items-center gap-2 font-sans text-[11px] tracking-[0.16em] uppercase text-canvas/60 hover:text-gold transition-colors"
          >
            <ArrowLeft className="w-4 h-4" strokeWidth={1.6} />
            Back
          </button>
          <span className="font-sans text-[11px] tracking-[0.28em] uppercase text-gold">Enquiries</span>
        </div>
      </header>

      <main className="max-w-[1000px] mx-auto px-6 lg:px-10 py-14">
        <h1 className="font-serif text-3xl md:text-4xl text-charcoal leading-tight mb-2">Enquiries</h1>
        <p className="font-sans text-sm text-charcoal/55 mb-10">
          {items ? `${items.length} received` : "Loading…"}
        </p>

        {items && items.length === 0 && (
          <div data-testid="enquiries-empty" className="border border-dashed border-charcoal/20 bg-canvas py-24 text-center">
            <Inbox className="w-10 h-10 text-charcoal/25 mx-auto" strokeWidth={1.2} />
            <p className="mt-4 font-serif text-lg text-charcoal/60">No enquiries yet.</p>
          </div>
        )}

        <div className="space-y-px bg-charcoal/10 border border-charcoal/10">
          {(items || []).map((it) => (
            <div key={it.id} data-testid={`enquiry-row-${it.id}`} className="bg-canvas p-6 flex flex-col sm:flex-row sm:items-start gap-4">
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-3 flex-wrap">
                  <a href={`mailto:${it.email}`} className="inline-flex items-center gap-2 font-serif text-lg text-charcoal hover:text-forest transition-colors">
                    <Mail className="w-4 h-4 text-forest" strokeWidth={1.6} />
                    {it.email}
                  </a>
                  <span
                    data-testid={`enquiry-source-${it.id}`}
                    className={`font-sans text-[10px] tracking-[0.14em] uppercase px-2 py-0.5 border ${
                      it.source === "chatbot"
                        ? "border-gold/60 text-gold bg-gold/10"
                        : "border-forest/40 text-forest bg-forest/5"
                    }`}
                  >
                    {it.source === "chatbot" ? "Chatbot" : "Contact form"}
                  </span>
                  {it.name && (
                    <span className="font-sans text-[11px] tracking-[0.14em] uppercase text-charcoal/60">{it.name}</span>
                  )}
                  {it.company && (
                    <span className="font-sans text-[11px] tracking-[0.14em] uppercase text-charcoal/45">{it.company}</span>
                  )}
                </div>
                {it.message && (
                  <p className="mt-3 font-serif text-base text-charcoal/75 leading-relaxed whitespace-pre-wrap">{it.message}</p>
                )}
                <p className="mt-3 font-sans text-[11px] tracking-wide text-charcoal/40">
                  {new Date(it.created_at).toLocaleString()}
                </p>
              </div>
              <button
                data-testid={`delete-enquiry-${it.id}`}
                onClick={() => remove(it)}
                className="inline-flex items-center justify-center w-10 h-10 border border-charcoal/15 text-charcoal/70 hover:border-red-500 hover:text-red-500 transition-colors shrink-0"
              >
                <Trash2 className="w-4 h-4" strokeWidth={1.6} />
              </button>
            </div>
          ))}
        </div>
      </main>
    </div>
  );
}
